import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFitStore } from '../../store/useFitStore';

/* ============================================================
   First-run product tour — dependency-free spotlight walkthrough.

   The store's startTour() overlays the demo dataset on the real
   signed-in coach (writes suppressed via isLive()), so every page
   the tour visits renders live-looking mock data. The engine
   navigates between routes, anchors a popover to [data-tour=…]
   targets with a box-shadow spotlight cutout, and on Finish tears
   the sandbox down and opens the Add-clients wizard
   (/coach?import=1 — existing deep link).
============================================================ */

interface TourStep {
  route: string;
  target?: string;        // data-tour value; omitted = centered card
  title: string;
  body: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    route: '/coach',
    title: 'Welcome to FitFlex',
    body: 'Ninety seconds, the whole loop — including a few features you\'d never find on your own. This is sample data; nothing in the tour is saved.',
  },
  {
    route: '/coach',
    target: 'roster',
    title: 'Your clients',
    body: 'The roster lives here — status dots show who is on track, and "due" flags an outstanding weekly check-in.',
  },
  {
    route: '/coach',
    target: 'week-grid',
    title: 'Plan the week',
    body: 'Each client\'s training week. Click any day to program a session — or log one live, mid-workout, with an exercise menu that prefills their last numbers.',
  },
  {
    route: '/coach',
    target: 'client-hub',
    title: 'One client, one workspace',
    body: 'Click the client\'s name to open everything about them: profile, notes, weekly check-in, transformation photos — and a case-study builder that turns their real results into a shareable image.',
  },
  {
    route: '/coach',
    target: 'client-hub',
    title: '✦ Meet your Advisor',
    body: 'Inside that workspace lives an AI that has read the client\'s whole file. Chat with it — "which schedule got the best results?" — or have it research the science and cite sources. Premium, free during beta.',
  },
  {
    route: '/coach/client/c1/log',
    target: 'trend',
    title: 'Progress you can feel',
    body: 'The full training log: weight, body fat, and muscle charts you can scrub like a stock ticker, plus every session ever logged.',
  },
  {
    route: '/coach/overview',
    target: 'digest',
    title: 'Today',
    body: 'Your morning briefing: appointments, check-ins due, and lead follow-ups — each one tap from done.',
  },
  {
    route: '/coach/calendar',
    target: 'calendar',
    title: 'Schedule',
    body: 'Four weeks at a glance. "⇅ Google" pulls your existing Google Calendar in — sessions map to clients automatically — and any appointment can send the client a Google invite.',
  },
  {
    route: '/coach/leads',
    target: 'leads',
    title: 'Leads',
    body: 'Prospects move new → consult → won, and FitFlex nudges you when a follow-up is due.',
  },
  {
    route: '/coach/library',
    target: 'library',
    title: 'Library',
    body: 'Reusable programs and a searchable exercise library make planning and live logging fast.',
  },
  {
    route: '/coach',
    title: 'That\'s the loop.',
    body: 'Plan, train, grow — now make it yours. Drop in any spreadsheet, or a whole training-log workbook: AI maps it, and a client\'s full history imports in one click.',
  },
];

const doneKey = (userId: string) => `fitflex_tour_done:${userId}`;

/* Find the VISIBLE element for a data-tour value (TopNav/BottomNav can both
   carry the same anchor — pick the one actually rendered). */
const findTarget = (name: string): HTMLElement | null => {
  const els = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`));
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width > 2 && r.height > 2) return el;
  }
  return null;
};

const TourOverlay: React.FC = () => {
  const navigate = useNavigate();
  const { user, endTour } = useFitStore();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const pollRef = useRef<number | undefined>(undefined);

  const step = TOUR_STEPS[i];
  const last = i === TOUR_STEPS.length - 1;

  // Per-step: navigate, then poll for the target (it mounts after the route
  // renders) and keep tracking it (resize, fonts, async content).
  useEffect(() => {
    navigate(step.route);
    setRect(null);
    if (!step.target) return;
    let tries = 0;
    let scrolled = false;
    const tick = () => {
      const el = findTarget(step.target!);
      if (el) {
        if (!scrolled) {
          scrolled = true;
          el.scrollIntoView({ block: 'center' });
        }
        setRect(el.getBoundingClientRect());
      }
      if (el || tries++ < 40) pollRef.current = window.setTimeout(tick, el ? 300 : 50);
    };
    tick();
    return () => window.clearTimeout(pollRef.current);
  }, [i]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = (openImport: boolean) => {
    if (user) localStorage.setItem(doneKey(user.id), '1');
    endTour();
    navigate(openImport ? '/coach?import=1' : '/coach');
  };

  /* ---------- popover placement ---------- */
  const pad = 8;
  const centered = !step.target || !rect;
  const popStyle: React.CSSProperties = { position: 'fixed', zIndex: 10002, width: 'min(360px, calc(100vw - 32px))' };
  if (centered) {
    Object.assign(popStyle, { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' });
  } else if (window.innerWidth < 640) {
    Object.assign(popStyle, { left: 16, right: 16, bottom: 16, width: 'auto' });
  } else {
    // Place below or above the target only when the popover actually fits;
    // a near-full-height target (tablet roster column) gets it floated INSIDE
    // the viewport instead of anchored off the top edge.
    const POP_H = 220; // generous popover height estimate
    const spaceBelow = window.innerHeight - rect!.bottom;
    const spaceAbove = rect!.top;
    const left = Math.max(16, Math.min(rect!.left, window.innerWidth - 376));
    if (spaceBelow >= POP_H) {
      Object.assign(popStyle, { top: rect!.bottom + 12, left });
    } else if (spaceAbove >= POP_H) {
      Object.assign(popStyle, { bottom: window.innerHeight - rect!.top + 12, left });
    } else {
      Object.assign(popStyle, { top: Math.max(16, window.innerHeight / 2 - POP_H / 2), left });
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }} aria-modal>
      {/* scrim + spotlight cutout (the box-shadow trick) */}
      {centered ? (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(2px)' }} />
      ) : (
        <div
          style={{
            position: 'fixed',
            left: rect!.left - pad,
            top: rect!.top - pad,
            width: rect!.width + pad * 2,
            height: rect!.height + pad * 2,
            borderRadius: 12,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
            border: '1px solid rgba(255,255,255,0.35)',
            transition: 'all 0.35s cubic-bezier(0.16,1,0.3,1)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* popover */}
      <div style={popStyle} className="card" onClick={(e) => e.stopPropagation()}>
        <p className="mono-label mb-2">{i + 1} / {TOUR_STEPS.length}</p>
        <h3 className="text-[18px] font-bold tracking-tight mb-1">{step.title}</h3>
        <p className="text-[13px] text-fit-text-secondary leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => finish(false)} className="text-[12px] text-fit-muted hover:text-white">
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={() => setI(i - 1)} className="btn btn-glass btn-sm">← Back</button>
            )}
            {last ? (
              <button onClick={() => finish(true)} className="btn btn-primary btn-sm">Finish — add my clients</button>
            ) : (
              <button onClick={() => setI(i + 1)} className="btn btn-primary btn-sm">{i === 0 ? 'Show me' : 'Next'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* Gate: auto-start the tour for a coach whose roster is empty and who hasn't
   seen it; render the overlay while the tour runs. Lives inside the Router. */
const TourGate: React.FC = () => {
  const { user, dataLoaded, clients, tourMode, startTour } = useFitStore();
  // The beta welcome goes first; its dismissal event re-runs this gate.
  const [betaBump, setBetaBump] = useState(0);
  useEffect(() => {
    const onDismiss = () => setBetaBump((n) => n + 1);
    window.addEventListener('fitflex:beta-dismissed', onDismiss);
    return () => window.removeEventListener('fitflex:beta-dismissed', onDismiss);
  }, []);

  useEffect(() => {
    if (!user || tourMode) return;
    const isCoach = user.role === 'coach' || user.role === 'owner';
    if (!isCoach || !dataLoaded || clients.length > 0) return;
    if (!localStorage.getItem(`fitflex_beta_welcome:${user.id}`)) return; // welcome first
    if (localStorage.getItem(doneKey(user.id))) return;
    startTour();
  }, [user, dataLoaded, clients.length, tourMode, startTour, betaBump]);

  return tourMode ? <TourOverlay /> : null;
};

export default TourGate;
